/* eslint-disable import/no-unresolved */
import {useCallback, useEffect, useReducer, useState} from 'react';
import dynamic from 'next/dynamic';
import {useRouter} from 'next/router';

import {useApp} from '@context/AppProvider';
import {api} from '@utils/api';
import {excludeGSTPrice, joinArray} from '@utils/functions';
import {hasFreeShipping} from '@utils/product';
import {useGoodCause} from '@context/GoodCauseProvider';
import {init, reducer} from '@reducers/product/detail/index.js';
import Loading from '@components/Loading';
import RelatedProduct from '@components/product/detail/RelatedProduct';
import ShareProduct from '@components/product/detail/ShareProduct';

import {
	setInitialPrice,
	calculateGoodCauseValue,
	toggleModalVisibility,
	seeComparePrices,
	getAuction,
} from './functions';
import {
	closeAddressModal,
	closeBuyBeforeModal,
	closeCardModal,
	closeGoodCauseModal,
	closeShippingModal,
	fetchCustomerCards,
	fetchGoodCauses,
	nextStep,
	onClickShowAddressModal,
	onSelectShippingOption,
	pickGoodCause,
	selectGoodCause,
} from './dropauction/functions';

const ImageSlider = dynamic(() =>
	import('@components/product/detail/ImageSlider'),
);

const Breadcrumbs = dynamic(() =>
	import('@components/product/detail/Breadcrumbs'),
);
const SelectVariant = dynamic(() =>
	import('@components/product/detail/SelectVariant'),
);
const ComparePrice = dynamic(() =>
	import('@components/product/detail/ComparePrice'),
);
const QuantitySelector = dynamic(() =>
	import('@components/product/detail/QuantitySelector'),
);
const PromoModal = dynamic(() => import('@models/promo.modal'));
const BagModal = dynamic(() => import('@models/addToBag.modal'));
const GuideModal = dynamic(() => import('@models/guide.modal'));
const ShippingModal = dynamic(() => import('@models/shipping.modal'));
const PaymentModal = dynamic(() => import('@models/addNewCard.modal'));
const AddressModal = dynamic(() => import('@models/AddressAddEdit.modal'));
const SelectVariantError = dynamic(() =>
	import('@components/product/detail/SelectVariantError'),
);
const BrandRetailerLogo = dynamic(() =>
	import('@components/product/detail/BrandRetailerLogo'),
);
const ProductInfo = dynamic(() =>
	import('@components/product/detail/ProductInfo/index'),
);
const SelectGoodCauseModalOne = dynamic(() =>
	import('@models/selectGoodCause.modal'),
);

const BuyBeforeAuctionModal = dynamic(() =>
	import('@models/BuyBeforeAuction.modal'),
);

const ActionButtons = dynamic(() =>
	import('@components/product/detail/ActionButtons'),
);
const FreeShipping = dynamic(() =>
	import('@components/product/detail/FreeShipping'),
);
const AuctionHowToWork = dynamic(() =>
	import('@components/product/detail/dropauction/HowToWork'),
);
const AuctionReminder = dynamic(() =>
	import('@components/product/detail/dropauction/Reminder'),
);
const MessageComponent = dynamic(() =>
	import('@components/common/MessageComponent'),
);
const JoinDrop = dynamic(() =>
	import('@components/product/detail/dropauction/joinDrop'),
);

const RateProduct = dynamic(() =>
	import('@components/product/detail/RateProduct'),
);

const PromoCode = dynamic(() => import('@components/product/detail/PromoCode'));

const AlocholInfo = dynamic(() =>
	import('@components/product/detail/AlocholInfo'),
);

function ProductDetail(props) {
	const {
		styles,
		product,
		brand,
		mas,
		promotions,
		settings,
		currencyData,
		queryParams,
		isStore,
		isBrand,
		isModal = false,
		hideModal,
		store,
		initData = null,
	} = props;
	const [state, dispatch] = useReducer(reducer, init);
	const {
		idb: {origin, session, user, currency, isoCode},
		convertCurrency,
	} = useApp();
	const router = useRouter();
	const [loading, setLoading] = useState(true);
	const {gc} = useGoodCause();
	const getPercentage = useCallback(() => {
		const pOff =
			((state.strikeThroughPrice - state.rrp) / state.strikeThroughPrice) * 100;
		if (pOff < 1) {
			return 1;
		} else {
			return Math.round(pOff);
		}
	}, [state.strikeThroughPrice, state.rrp]);
	const checkDefaultVariant = useCallback(() => {
		if (product.variances.length > 0 && product.variances[0].is_base_variance) {
			dispatch({
				type: 'setData',
				data: {
					selectedVariance: product.variances[0]._id,
					varianceName: '',
				},
			});
		}
	}, [product.variances, dispatch]);

	const getAuctionDistance = useCallback(
		d => {
			const timestamp = d / 60 / 1000;
			if (timestamp <= 10) {
				dispatch({
					type: 'setData',
					data: {auctionTenMinLeft: true},
				});
			}
		},
		[dispatch],
	);

	const setActiveAuctionData = useCallback(() => {
		if (user) {
			fetchCustomerCards(session, dispatch);
			fetchGoodCauses(session, dispatch);
		}
	}, [user, session, dispatch]);

	const handleComparePrice = useCallback(
		(product, session, state, dispatch, origin, isEffect) => {
			return seeComparePrices(
				product,
				session,
				state,
				dispatch,
				origin,
				isoCode,
				isEffect,
			);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[currency],
	);

	function displayShippingCountry(countries) {
		const result = countries?.filter(checkCountries);
		function checkCountries(country) {
			return country === 'Australian' || country === 'New Zealand';
		}
		if (result?.length === 1) {
			return result[0];
		} else {
			return false;
		}
	}

	const getMessageAvailShipping = useCallback(() => {
		let availableShipping = '';
		if (
			product?.retailers.length === 1 ||
			(isStore && product?.retailers.length)
		) {
			const shipCountry =
				state.availableShippingCountries[product?.retailers[0]._id];
			if (displayShippingCountry(shipCountry?.countries)) {
				availableShipping = `This item can be shipped to ${displayShippingCountry(
					shipCountry?.countries,
				)} addresses only.`;
			}
		}
		return availableShipping;
	}, [product?.retailers, isStore, state.availableShippingCountries]);

	useEffect(() => {
		handleComparePrice(product, session, state, dispatch, origin, true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currency]);

	useEffect(() => {
		const initial = async () => {
			try {
				let obj = {
					settings: settings,
				};

				if (product.retailer_products.length === 1) {
					obj.retailer = product.retailers.find(
						r => r._id === product.retailer_products[0].retailer_id,
					);
					obj.isSeeOptionActive = true;
					if (product && product.auctions.length > 0) {
						obj.activeDropAuction = product.auctions[0];
						obj.activeDropAuction.tempPirce = obj.activeDropAuction.base_price;
						obj.strikeThroughPrice = 0;
					}
				}
				setActiveAuctionData();
				if (!obj.activeDropAuction) {
					const result = setInitialPrice(
						isStore,
						product,
						obj.retailer,
						isoCode,
						settings,
						origin,
						convertCurrency,
					);
					obj = {...obj, ...result};
				}

				obj.goodCauseValue = calculateGoodCauseValue(
					settings,
					isoCode,
					product,
					convertCurrency(
						excludeGSTPrice(
							Number(
								obj.activeDropAuction
									? obj.activeDropAuction.base_price
									: obj.rrp,
							),
							origin,
						).toFixed(2),
						false,
						false,
					),
					isStore,
				);
				// let guide = '';
				// if (brand && brand.guide) guide = brand.guide;
				if (product?.product_tabs && product.product_tabs.length)
					obj.tabs = product.product_tabs;

				// if (guide) obj.sizeGuide = guide;
				if (product?.retailerLevelGuide && product?.retailerLevelGuide.slug) {
					obj.retailerGuide = product?.retailerLevelGuide.slug;
				}
				const cartResult = await api(
					{
						url: '/cart',
						method: 'GET',
					},
					session,
				);

				if (cartResult && cartResult.cart) {
					obj.cart = cartResult.cart.products;
				}
				if (product.wishlistData) obj.wishlistData = product.wishlistData;
				checkDefaultVariant();

				dispatch({type: 'setData', data: {...obj}});
				setLoading(false);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.log(error);
			}
		};
		if (origin) {
			if (initData) {
				dispatch({type: 'setData', data: {...initData}});
				setLoading(false);
			} else {
				initial();
			}
		}
		return () => {
			if (origin && isModal) {
				dispatch({type: 'setData', data: {...init}});
			}
		};
	}, [
		brand,
		origin,
		isoCode,
		product,
		session,
		settings,
		currencyData,
		isStore,
		isModal,
		checkDefaultVariant,
		setActiveAuctionData,
		convertCurrency,
		initData,
	]);

	useEffect(() => {
		if (state.addToCart && hideModal) {
			let timeout = setTimeout(() => {
				hideModal(true);
				clearTimeout(timeout);
			}, 2000);
		}
	}, [hideModal, state.addToCart]);

	useEffect(() => {
		if (
			state.isInstantBidButtonClicked &&
			(!state.selectedPaymentOption ||
				!state.selectedShippingOption ||
				!state.selectedGoodCause)
		) {
			nextStep(state.instantBidTries, state, dispatch);
		} else if (state.isInstantBidButtonClicked) {
			nextStep(state.instantBidTries, state, dispatch);
		}

		// if (state.selectedGoodCause) {
		// 	updateGoodCause({
		// 		_id: state.selectedGoodCause._id,
		// 		name: state.selectedGoodCause.registeredCharityName,
		// 	});
		// }
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		state.isInstantBidButtonClicked,
		state.selectedPaymentOption,
		state.selectedShippingOption,
		state.selectedGoodCause,
	]);

	useEffect(() => {
		if (product.variances.length && !product.variances[0].is_base_variance) {
			if (
				product.retailer_products.length === 1 &&
				product.auctions &&
				product.auctions.length > 0
			) {
				const goodCause = gc ? gc : null;
				if (state.selectedVariance) {
					const auction = getAuction(product, state.selectedVariance);
					const obj = {
						activeDropAuction: auction,
						counterVisiblity: !auction ? true : false,
						selectedGoodCause: goodCause,
					};
					dispatch({type: 'setData', data: {...obj}});
				} else {
					let auction = product.auctions[0];
					// eslint-disable-next-line babel/camelcase
					if (auction.tempPirce) auction.base_price = auction.tempPirce;
					const obj = {
						activeDropAuction: auction,
						counterVisiblity: false,
						selectedGoodCause: goodCause,
					};
					dispatch({type: 'setData', data: {...obj}});
				}
			} else {
				if (!state.selectedVariance) {
					const obj = {
						activeDropAuction: null,
					};
					dispatch({type: 'setData', data: {...obj}});
				} else if (!state.isSeeOptionActive && product.isRPActive > 1) {
					const obj = {
						activeDropAuction: null,
					};
					dispatch({type: 'setData', data: {...obj}});
				}
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		product,
		product.auctions,
		product.retailer_products.length,
		product.variances,
		state.activeDropAuction,
		state.isSeeOptionActive,
		state.selectedVariance,
	]);

	const navigateProduct = () => {
		sessionStorage.setItem('productState', JSON.stringify(state));
		router.push(
			store
				? `/product/store/${store}/${product.slug}`
				: `/product/${product.slug}`,
		);
	};

	const fetchRetailerShippingCountries = useCallback(async () => {
		let obj = {};
		let objB = {};
		for (let retailer of product.retailers) {
			const shipingResult = await api(
				{
					url: '/cart/get-available-shiping-countries',
					method: 'POST',
					data: {
						productId: [product._id],
						retailerId: retailer._id,
					},
				},
				session,
			);
			obj[retailer._id] = {
				countries: shipingResult?.availableCountries,
				str: joinArray(shipingResult?.availableCountries, `/`),
			};

			objB[retailer._id] = {
				countries: shipingResult?.unavailableCountries,
				str: joinArray(shipingResult?.unavailableCountries, `/`),
			};
		}
		dispatch({
			type: 'setData',
			data: {
				availableShippingCountries: obj,
				unavailableShippingCountries: objB,
			},
		});
	}, [product._id, product.retailers, session]);

	useEffect(() => {
		if (session) fetchRetailerShippingCountries();
	}, [fetchRetailerShippingCountries, session]);

	useEffect(() => {
		const str = Object.keys(state?.availableShippingCountries)
			.map(retailer => {
				return state?.availableShippingCountries[retailer]?.countries?.join('');
			})
			.join('');

		dispatch({
			type: 'setData',
			data: {
				isOnlyAus: str === 'Australian',
			},
		});
	}, [state?.availableShippingCountries]);

	return (
		<>
			{!!product.retailers.length && !isModal && (
				<FreeShipping
					retailer={product.retailers[0]}
					styles={styles}
					isOnlyAus={state?.isOnlyAus}
					retailerProducts={product.retailer_products}
				/>
			)}
			<div className={styles.detailPageBlock}>
				<div className={styles.pageHeading}></div>
				<div className='container'>
					{!isModal && (
						<Breadcrumbs
							styles={styles}
							categories={product?.categories}
							slug={queryParams?.category}
							productId={product?._id}
							sizeGuideId={null}
							sizeGuide={state.sizeGuide}
							dispatch={dispatch}
							isStore={isStore}
							retailer={state.retailer}
							isBrand={isBrand}
							brand={product?.manufacturer}
						/>
					)}
					<div className={styles.detailInfoBlock}>
						<div className='row'>
							<div
								className={`col-md-12  ${isModal ? `col-lg-6` : 'col-lg-7'}`}
							>
								<ImageSlider
									styles={styles}
									images={product?.images}
									variants={product?.variances}
									state={state}
									dispatch={dispatch}
									productName={product?.product_name}
									hasAuction={state.activeDropAuction}
									isModal={isModal}
								/>
							</div>
							<div className={`col-md-12 ${isModal ? `col-lg-6` : 'col-lg-5'}`}>
								<div className={styles.productDetailInfo}>
									{/* brand from retailer */}
									<BrandRetailerLogo
										styles={styles}
										product={product}
										isModal={isModal}
										state={state}
										hideModal={hideModal}
										dispatch={dispatch}
									/>

									<div className={`${styles.productNameHeading} `}>
										{product.product_name}
									</div>

									<div className={styles.buyPriceBlock}>
										{(state.counterVisiblity || state.activeDropAuction) && (
											<div className={styles.auctionProductName}>
												<JoinDrop
													getAuctionDistance={getAuctionDistance}
													product={product}
													state={state}
													styles={styles}
												/>
												{state.activeDropAuction && !loading && (
													<div className={styles.auctionBlock}>
														<AuctionReminder
															auction={state.activeDropAuction}
															productId={product._id}
															state={state}
															dispatch={dispatch}
															isModal={isModal}
															hideModal={hideModal}
														/>
													</div>
												)}
												{!!state.activeDropAuction && (
													<h2>
														Dropping from
														{convertCurrency(
															state.activeDropAuction.base_price.toFixed(2),
														)}
													</h2>
												)}
											</div>
										)}

										<div className={styles.priceBuyOuter}>
											{!loading &&
												!state.activeDropAuction &&
												!state.selectedStoreShop && (
													<h2 className='mb-0'>
														{!state.hasMultiPricing ||
														product.retailers.length > 1
															? 'From'
															: ''}
														{convertCurrency(state.rrp && state.rrp.toFixed(2))}
													</h2>
												)}

											{!loading &&
												!state.activeDropAuction &&
												state.selectedStoreShop && (
													<h2 className='mb-0'>
														{convertCurrency(
															state.selectedStoreShop.price &&
																state.selectedStoreShop.price.toFixed(2),
														)}
													</h2>
												)}

											{!loading &&
												!state.activeDropAuction &&
												!state.selectedStoreShop &&
												state.strikeThroughPrice > state.rrp && (
													<span className={styles.priceBuy}>
														<strong>
															<span dir='ltr'>
																{convertCurrency(
																	state.strikeThroughPrice.toFixed(2),
																)}{' '}
															</span>
														</strong>
														<span> Save {getPercentage()}%</span>
													</span>
												)}
										</div>

										{!loading && (
											<p style={{marginLeft: '0', marginBottom: '10px'}}>
												Better That will donate{' '}
												{state.activeDropAuction ? 'up to ' : ''}
												<strong>
													{convertCurrency(
														state.goodCauseValue.gcAmount.toFixed(2),
														true,
														true,
														false,
														false,
													)}
												</strong>{' '}
												to your Good Cause when you buy this.
											</p>
										)}

										{!loading &&
											state.retailer &&
											state.goodCauseValue.retailerGcAmount && (
												<p
													style={{
														marginLeft: '0',
														marginBottom: '10px',
													}}
													className={styles.retailerDonation}
												>
													<strong> {state.goodCauseValue.shop_name}</strong>{' '}
													will also donate{' '}
													{state.activeDropAuction ? 'up to ' : ''}
													<strong>
														{convertCurrency(
															state.goodCauseValue.retailerGcAmount.toFixed(2),
															true,
															true,
															false,
															false,
														)}
													</strong>{' '}
													to{' '}
													{state.goodCauseValue.retailerCharityId !== gc._id ? (
														<>
															<strong>
																{state.goodCauseValue.retailerGoodName}
															</strong>{' '}
															when you buy this.
														</>
													) : (
														<>your Good Cause when you buy this.</>
													)}
												</p>
											)}

										{isModal && (
											<div className='row ml-1'>
												<div className={styles.freeShipping}>
													<ul>
														{state.selectedStoreShop &&
															hasFreeShipping(state.selectedStoreShop) && (
																<li>Free Shipping</li>
															)}
														{!state.selectedStoreShop &&
															product.retailers.length === 1 &&
															hasFreeShipping({
																...product.retailers[0],
																price: state.rrp,
															}) && <li>Free Shipping</li>}
													</ul>
												</div>
											</div>
										)}

										{!isModal &&
											product.retailers.length === 1 &&
											!state.activeDropAuction &&
											promotions &&
											!!promotions.length && (
												<div
													className={`${styles.promoList} ${styles.promoListBorder}`}
												>
													<PromoCode
														promotions={promotions}
														close={() =>
															toggleModalVisibility(false, 'promo', dispatch)
														}
													></PromoCode>
												</div>
											)}
									</div>
									{product.variances.length &&
									!product.variances[0].is_base_variance ? (
										<SelectVariant
											styles={styles}
											variances={product?.variances}
											auctions={product?.auctions}
											mas={mas}
											currencyData={currencyData}
											retailers={product?.retailers}
											retailerProducts={product?.retailer_products}
											isStore={isStore}
											origin={origin}
											product={product}
											state={state}
											dispatch={dispatch}
											session={session}
											settings={settings}
											isoCode={isoCode}
											convertCurrency={convertCurrency}
										/>
									) : (
										<>
											{!state.activeDropAuction &&
												state.isSeeOptionActive &&
												state.selectedVariance &&
												state.retailer && (
													<div style={{marginBottom: '10px'}}>
														<QuantitySelector
															pId={product?._id}
															retailerProducts={product?.retailer_products}
															state={state}
															dispatch={dispatch}
														></QuantitySelector>
													</div>
												)}
											<div style={{paddingBottom: '7px'}}>
												<span className={styles.availShip}>
													{getMessageAvailShipping()}
												</span>
											</div>
										</>
									)}

									{!!(
										!state.selectedVariance &&
										state.isBtnClicked &&
										product.retailer_products.length === 1
									) && (
										<SelectVariantError
											activeAttributes={state.activeAttributes}
											selectedOptions={state.selectedOptions}
										/>
									)}
									{!state.isSeeOptionActive && product.isRPActive > 1 && (
										<div>
											<div className={styles.buyNowBlock}>
												<button
													className={`${styles.btnCustom} ${styles.comapreBtn}`}
													onClick={() =>
														handleComparePrice(
															product,
															session,
															state,
															dispatch,
															origin,
															false,
														)
													}
													disabled={state.loading}
												>
													<span>Compare Prices</span>
													{state.loading && <Loading.LoaderThree />}
												</button>
											</div>
											{!state.selectedVariance && (
												<div className={styles.infoTextStore}>
													<p>{`Please let us know what you're looking for first and we'll show you the stores that have what you are after!`}</p>
												</div>
											)}
										</div>
									)}

									{!!state.storePrices.length &&
										state.isSeeOptionActive &&
										product.retailer_products.length > 1 && (
											<ComparePrice
												product={product}
												isStore={isStore}
												isModal={isModal}
												loading={loading}
												removeWishlist={props.removeWishlist}
												state={state}
												dispatch={dispatch}
												origin={origin}
												currencyData={currencyData}
												settings={settings}
											></ComparePrice>
										)}

									{!loading &&
										(product.isRPActive === 1 ||
											(product.auctions && product.auctions.length > 0)) && (
											<ActionButtons
												styles={styles}
												isStore={isStore}
												product={product}
												state={state}
												dispatch={dispatch}
												isModal={isModal}
												disabled={state.addToCart}
												currencyData={currencyData}
												removeWishlist={props.removeWishlist}
											></ActionButtons>
										)}

									{state.addToCart && (
										<div className={styles.message}>
											<MessageComponent
												message={'Item added to Bag'}
												messageKey={'success'}
												itemAddedToBag={true}
											/>
										</div>
									)}
									{state?.stockError?.error && state.stockError.error && (
										<p className={`text-danger ${styles.textError}`}>
											<strong>{state.stockError.bagError}</strong>
										</p>
									)}

									{/* rate and share product */}
									{isModal && user ? (
										<RateAndShareProductSection product={product} />
									) : !isModal ? (
										<RateAndShareProductSection product={product} />
									) : (
										''
									)}

									{/* shipping return accordian */}
									{!isModal && (
										<ProductInfo
											dispatch={dispatch}
											state={state}
											styles={styles}
											product={product}
										/>
									)}
								</div>
								{isModal && (
									<>
										<div className={`links ${styles.viewPLink}`}>
											<span className={`link_type_9`} onClick={navigateProduct}>
												{`View full product details`}
											</span>
										</div>
										<AlocholInfo
											styles={styles}
											product={product}
											isModal={isModal}
										/>
									</>
								)}
							</div>
							{!isModal && (
								<AlocholInfo
									styles={styles}
									product={product}
									isModal={isModal}
								/>
							)}
						</div>
					</div>
				</div>

				{!isModal && state.activeDropAuction && <AuctionHowToWork />}
				{!isModal && (
					<RelatedProduct productId={product._id} query={queryParams} />
				)}
				{state.showPaymentModal && (
					<PaymentModal
						checkout={false}
						close={() => closeCardModal(dispatch)}
						getCustomerCards={() => fetchCustomerCards(session, dispatch)}
						meta={{
							title: 'Add payment method to make an offer',
							description:
								'Before you make an offer you will need to add a credit card as a payment method. You will only be charged if you win.',
							buttonText: 'ADD CARD',
						}}
					/>
				)}
				{state.showShippingModal && (
					<ShippingModal
						auction={state.activeDropAuction}
						retailerProducts={product.retailer_products}
						productId={product._id}
						masterState={state}
						dispatch={dispatch}
						showAddressEvent={() => onClickShowAddressModal(dispatch)}
						close={() => closeShippingModal(dispatch)}
						callback={params =>
							onSelectShippingOption(params, state, dispatch, user)
						}
					/>
				)}
				{state.showAddressModal && (
					<AddressModal
						close={(params, id = null) =>
							closeAddressModal(params, id, user, session, dispatch)
						}
						edit={false}
						address={''}
						totalAddress={0}
						auctionCheck={false}
					/>
				)}
				{state.showGoodCauseModalOne && (
					<SelectGoodCauseModalOne
						callBack={data => pickGoodCause(data, state, dispatch)}
						close={data => closeGoodCauseModal(data, dispatch)}
						user={user}
						session={session}
						callBackGoodCause={data => selectGoodCause(data, state, dispatch)}
					/>
				)}

				{state.showBuyBeforeAuctionModal && (
					<BuyBeforeAuctionModal
						auctionId={state.activeDropAuction?._id}
						user={user}
						session={session}
						product={product}
						settings={settings}
						state={state}
						close={() => closeBuyBeforeModal(dispatch)}
					/>
				)}
				{state.showPromoPopup && (
					<PromoModal
						promotions={promotions}
						close={() => toggleModalVisibility(false, 'promo', dispatch)}
					></PromoModal>
				)}
				{state.openBagModal && (
					<BagModal
						product={state.addedCartProduct}
						close={() => toggleModalVisibility(false, 'bag', dispatch)}
					></BagModal>
				)}
				{state.showGuideModal && (
					<>
						<GuideModal
							guide={state.sizeGuide}
							product={product}
							close={() => toggleModalVisibility(false, 'guide', dispatch)}
						></GuideModal>
						<div id='dynamicstyle'></div>
					</>
				)}
			</div>
			{loading && !isModal && <Loading />}
		</>
	);
}

function RateAndShareProductSection(props) {
	const {product} = props;
	return (
		<div className='row'>
			<div className='col-sm-6'>
				<RateProduct product={product} />
			</div>
			<div className='col-sm-6'>
				<ShareProduct product={product} />
			</div>
		</div>
	);
}

export default ProductDetail;
